import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useCallback } from 'react'
import type { FieldLibraryDocument } from '@/types'
import { adminFieldLibraryService } from '../services/admin.fieldLibrary.service'
import type { FieldLibraryFormValues } from '../types'

interface UseAdminFieldLibraryResult {
  fields: Array<FieldLibraryDocument & { id: string }>
  loading: boolean
  error: string | null
  refresh: () => void
  createField: (data: FieldLibraryFormValues) => Promise<string>
  updateField: (id: string, data: FieldLibraryFormValues) => Promise<void>
  deleteField: (id: string) => Promise<void>
}

export function useAdminFieldLibrary(): UseAdminFieldLibraryResult {
  const [fields, setFields] = useState<Array<FieldLibraryDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminFieldLibraryService
      .getAll()
      .then(setFields)
      .catch((err) => setError(formatUserFacingError(err, 'Failed to load field library')))
      .finally(() => setLoading(false))
  }, [tick])

  const createField = useCallback(async (data: FieldLibraryFormValues) => {
    const id = await adminFieldLibraryService.create(data)
    refresh()
    return id
  }, [refresh])

  const updateField = useCallback(async (id: string, data: FieldLibraryFormValues) => {
    await adminFieldLibraryService.update(id, data)
    refresh()
  }, [refresh])

  const deleteField = useCallback(async (id: string) => {
    await adminFieldLibraryService.delete(id)
    refresh()
  }, [refresh])

  return { fields, loading, error, refresh, createField, updateField, deleteField }
}
